import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook for managing API request cancellation
 * Provides methods to register, track, and cancel API requests
 */
interface UseApiCancellationReturn {
  /**
   * Register an abortable promise with a unique key
   * @param promise - The promise to register (should have an abort method)
   * @param key - Unique identifier for this request
   * @returns The same promise for chaining
   */
  registerAbort: <T extends { abort?: () => void }>(
    promise: T,
    key: string
  ) => T;
  /**
   * Cancel a specific request by its key
   * @param key - The unique identifier of the request to cancel
   */
  cancelRequest: (key: string) => void;
  /**
   * Cancel all registered requests
   */
  cancelAllRequests: () => void;
}

const useApiCancellation = (): UseApiCancellationReturn => {
  // Map of request keys to their abort controllers/functions
  const abortMapRef = useRef<Map<string, () => void>>(new Map());

  // Clean up all requests on unmount
  useEffect(
    () => () => {
      abortMapRef.current.forEach((abort) => {
        abort();
      });
      abortMapRef.current.clear();
    },
    []
  );

  const registerAbort = useCallback(
    <T extends { abort?: () => void }>(promise: T, key: string): T => {
      // Cancel any existing request with the same key
      const existingAbort = abortMapRef.current.get(key);

      if (existingAbort) {
        existingAbort();
      }

      // Register the abort function if it exists
      // RTK Query mutations have an abort method, check for it
      if (typeof promise.abort === 'function') {
        abortMapRef.current.set(key, () => {
          promise.abort?.();
        });
      }

      return promise;
    },
    []
  );

  const cancelRequest = useCallback((key: string) => {
    const abort = abortMapRef.current.get(key);

    if (abort) {
      abort();
      abortMapRef.current.delete(key);
    }
  }, []);

  const cancelAllRequests = useCallback(() => {
    abortMapRef.current.forEach((abort) => {
      abort();
    });
    abortMapRef.current.clear();
  }, []);

  return {
    registerAbort,
    cancelRequest,
    cancelAllRequests,
  };
};

export { useApiCancellation };
export type { UseApiCancellationReturn };
